import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PlusIcon } from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import {
  fetchCompanyEmployeePositions,
  syncCompanyEmployeePositions,
} from "../../../api/companyEmployeePositions";
import ModernEmployeeSelector from "../../utils/Selector/ModernEmployeeSelector";
import { ResourceLoading } from "../../utils/ResourceLoading";

const emptyEmployeePosition = () => ({
  id: null,
  name: "",
  category: "",
  active: true,
  sort_order: 0,
  default_task_assignee_user_id: null,
});

export const CompanyEmployeePositionsEditor = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [employeePositions, setEmployeePositions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedEmployeesByRow, setSelectedEmployeesByRow] = useState({});
  const [employeePickerOpen, setEmployeePickerOpen] = useState(false);
  const [employeePickerIndex, setEmployeePickerIndex] = useState(null);

  const loadPositions = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchCompanyEmployeePositions();
      setEmployeePositions(Array.isArray(data.data) ? data.data : []);
      setCategories(Array.isArray(data.categories) ? data.categories : []);

      const users = {};
      (data.data || []).forEach((row) => {
        if (row?.default_task_assignee) {
          users[row.default_task_assignee.id] = row.default_task_assignee;
        }
      });
      setSelectedEmployeesByRow(users);
    } catch {
      toast.error(t("todo.settingsLoadError"));
      setEmployeePositions([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadPositions();
  }, [loadPositions]);

  const updateEmployeePosition = (index, patch) => {
    setEmployeePositions((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const addEmployeePosition = () => {
    setEmployeePositions((prev) => [...prev, emptyEmployeePosition()]);
  };

  const removeEmployeePosition = (index) => {
    setEmployeePositions((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = await syncCompanyEmployeePositions(
        employeePositions
          .filter((row) => row.name?.trim())
          .map((row) => ({
            id: row.id,
            name: row.name.trim(),
            category: row.category?.trim() || "",
            active: row.active !== false,
            sort_order: row.sort_order || 0,
            default_task_assignee_user_id: row.default_task_assignee_user_id || null,
          }))
      );
      setEmployeePositions(Array.isArray(data.data) ? data.data : []);
      setCategories(Array.isArray(data.categories) ? data.categories : []);
      toast.success(t("company.employee_positions.saved"));
    } catch {
      toast.error(t("todo.settingsSaveError"));
    } finally {
      setSaving(false);
    }
  };

  const routePickerSelected = useMemo(() => {
    if (employeePickerIndex == null) return [];
    const row = employeePositions[employeePickerIndex];
    const userId = row?.default_task_assignee_user_id;
    if (!userId) return [];
    return [selectedEmployeesByRow[userId] || row?.default_task_assignee || { id: userId, name: `#${userId}` }];
  }, [employeePickerIndex, employeePositions, selectedEmployeesByRow]);

  if (loading) {
    return <ResourceLoading />;
  }

  return (
    <div className="mt-5 overflow-visible rounded-md shadow">
      <div className="space-y-6 rounded-md bg-white py-6 px-4 sm:p-6">
        <div>
          <h3 className="text-lg font-medium leading-6 text-gray-900">
            {t("todo.employeePositionsTitle")}
          </h3>
          <p className="mt-1 text-sm text-gray-500">{t("company.employee_positions.help")}</p>
          <p className="mt-2 text-sm text-gray-500">
            {t("company.employee_positions.employment_link")}{" "}
            <Link to="/dashboard/users" className="font-medium text-blue-600 hover:text-blue-700">
              {t("company.employee_positions.employment_link_action")}
            </Link>
          </p>
        </div>

        <div className="space-y-3">
          {employeePositions.map((row, index) => (
            <div
              key={row.id || `new-pos-${index}`}
              className="rounded-lg border border-gray-200 bg-gray-50/40 p-4"
            >
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    {t("todo.employeePositionName")}
                  </label>
                  <input
                    className="block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    value={row.name || ""}
                    onChange={(e) => updateEmployeePosition(index, { name: e.target.value })}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    {t("todo.settingsCategory")}
                  </label>
                  <input
                    className="block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    value={row.category || ""}
                    list="company-position-category-options"
                    onChange={(e) => updateEmployeePosition(index, { category: e.target.value })}
                  />
                </div>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                  onClick={() => {
                    setEmployeePickerIndex(index);
                    setEmployeePickerOpen(true);
                  }}
                >
                  {row.default_task_assignee?.name || row.default_task_assignee_user_id
                    ? t("todo.settingsRouteToUser", {
                        name:
                          row.default_task_assignee?.name ||
                          selectedEmployeesByRow[row.default_task_assignee_user_id]?.name ||
                          `#${row.default_task_assignee_user_id}`,
                      })
                    : t("todo.settingsRouteAllTasks")}
                </button>
                {row.default_task_assignee_user_id ? (
                  <button
                    type="button"
                    className="text-xs font-medium text-gray-500 hover:text-gray-800"
                    onClick={() =>
                      updateEmployeePosition(index, {
                        default_task_assignee_user_id: null,
                        default_task_assignee: null,
                      })
                    }
                  >
                    {t("todo.settingsClearRoute")}
                  </button>
                ) : null}
                {row.employee_count ? (
                  <span className="text-xs text-gray-500">
                    {t("todo.employeePositionCount", { count: row.employee_count })}
                  </span>
                ) : null}
                <button
                  type="button"
                  className="ml-auto text-xs font-medium text-red-600 hover:text-red-700"
                  onClick={() => removeEmployeePosition(index)}
                >
                  {t("todo.employeePositionRemove")}
                </button>
              </div>
            </div>
          ))}
        </div>

        <datalist id="company-position-category-options">
          {categories.map((category) => (
            <option key={category} value={category} />
          ))}
        </datalist>

        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
            onClick={addEmployeePosition}
          >
            <PlusIcon className="h-4 w-4" />
            {t("todo.employeePositionAdd")}
          </button>
          <button
            type="button"
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:opacity-60"
            disabled={saving}
            onClick={handleSave}
          >
            {saving ? t("todo.settingsSaving") : t("todo.settingsSave")}
          </button>
        </div>
      </div>

      <ModernEmployeeSelector
        open={employeePickerOpen}
        useDialog
        multiple={false}
        selectedEmployees={routePickerSelected}
        onClose={() => {
          setEmployeePickerOpen(false);
          setEmployeePickerIndex(null);
        }}
        onChange={(employees) => {
          if (employeePickerIndex == null) return;
          const employee = employees[0];
          if (!employee?.id) return;
          updateEmployeePosition(employeePickerIndex, {
            default_task_assignee_user_id: employee.id,
            default_task_assignee: { id: employee.id, name: employee.name || employee.displayTitle },
          });
          setSelectedEmployeesByRow((prev) => ({ ...prev, [employee.id]: employee }));
        }}
      />
    </div>
  );
};

export default CompanyEmployeePositionsEditor;
